import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Space, Button, Radio, Typography, message } from "@mimber/ui-web"
import { Form, RadioGroupField } from "@mimber/forms"
import { useDeleteEvent } from "@mimber/client/mimber-organization"
import { EventEditScope } from "@mimber/schema"
import { DeleteEventFormSchema } from "./DeleteEventForm.schema"
import type { DeleteEventFormValues } from "./DeleteEventForm.schema"
import { SCOPE_OPTIONS } from "./DeleteEventForm.utils"
import type { DeleteEventFormProps } from "./DeleteEventForm.types"

const { Text, Paragraph } = Typography

export const DeleteEventForm: React.FunctionComponent<DeleteEventFormProps> = ({
  event,
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate()
  const isInSeries = !!event.eventSeriesId

  const form = useForm<DeleteEventFormValues>({
    resolver: zodResolver(DeleteEventFormSchema),
    mode: "onBlur",
    defaultValues: {
      scope: EventEditScope.THIS_EVENT,
    },
  })

  const mutation = useDeleteEvent(event.uuid, {
    onSuccess: () => {
      message.success("Event deleted successfully")
      onSuccess?.()
      navigate({ to: "/events" })
    },
    onError: (error) => {
      message.error(`Failed to delete event: ${error.message}`)
    },
  })

  const onSubmit = (data: DeleteEventFormValues) => {
    mutation.mutate(isInSeries ? { scope: data.scope } : {})
  }

  return (
    <Form form={form} onSubmit={onSubmit} layout="vertical">
      <Paragraph>
        Are you sure you want to delete <Text strong>{event.title}</Text>?
      </Paragraph>

      {isInSeries ? (
        <>
          <Paragraph type="secondary">
            This event is part of a series. Which events do you want to delete?
          </Paragraph>
          <RadioGroupField name="scope" disabled={mutation.isPending}>
            <Space direction="vertical">
              {SCOPE_OPTIONS.map((opt) => (
                <Radio key={opt.value} value={opt.value}>
                  <Space direction="vertical" size={0}>
                    <span>{opt.label}</span>
                    <Paragraph
                      type="secondary"
                      style={{ marginBottom: 0, fontSize: 12 }}
                    >
                      {opt.description}
                    </Paragraph>
                  </Space>
                </Radio>
              ))}
            </Space>
          </RadioGroupField>
        </>
      ) : (
        <Paragraph type="secondary">This action cannot be undone.</Paragraph>
      )}

      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button onClick={onCancel} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          type="primary"
          danger
          loading={mutation.isPending}
          disabled={mutation.isPending}
          htmlType="submit"
        >
          Delete Event
        </Button>
      </Space>
    </Form>
  )
}
